import * as React from 'react';

import { Card, CardContent } from 'components/ui/card';
import { DeliveryVoucherList, Title, VoucherInput } from './ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { CreateDeliveryVoucherActions as CreateAction } from 'store/ducks/deliveryVouchers/createDeliveryVoucher';
import {
  ListDeliveryVouchersActions,
  UpdateDeliveryVoucherActions,
} from 'store/ducks/deliveryVouchers';
import { RootState } from 'store';
import { useInvoiceStore } from '../../hooks';
import { DeliveryVoucher as IDeliveryVoucher } from 'contracts/DeliveryVouchers';
import { useMutation } from 'react-query';
import { useDeliveryVoucher } from 'hooks/api/delivery-vouchers/use-delivery-voucher';
import { RejectionProfPayload } from 'contracts/delivery-vouchers/delivery-voucher-justifications';
import { usePermission } from 'hooks/usePermission';
import { DELIVERY_VOUCHERS_PERMISSIONS } from 'constants/delivery-vouchers';
import { useDeliveryVoucherStore } from '../../hooks/use-delivery-voucher-store';
import { 
  ViewDeliveryVoucherDialog, 
  type ViewDeliveryVoucherDialogRef 
} from 'pages/tracking-v2/delivery-vouchers-page/components/view-delivery-voucher-dialog/view-delivery-voucher-dialog';

type Props = React.ComponentProps<typeof Card>;

export const DeliveryVoucher = ({ ...props }: Props) => {
  const [file, setFile] = React.useState<File | null>(null);
  const { downloadVoucher } = useDeliveryVoucher();
  const { hasPermissionTo } = usePermission();
  const canList = hasPermissionTo(
    DELIVERY_VOUCHERS_PERMISSIONS.getAllByInvoiceId
  );
  const { resetDeliveryVoucherStoreState } = useDeliveryVoucherStore();
  const canDownload = hasPermissionTo(DELIVERY_VOUCHERS_PERMISSIONS.download);
  const canUpdate = hasPermissionTo(DELIVERY_VOUCHERS_PERMISSIONS.update);

  const mutateDownload = useMutation(downloadVoucher);
  const dispatch = useDispatch();

  const { invoice } = useInvoiceStore();

  const { data: vouchers } = useSelector(
    (state: RootState) => state.listDeliveryVouchers
  );
  const { loading: creating } = useSelector(
    (state: RootState) => state.createDeliveryVoucher
  );
  const { loading: updating } = useSelector(
    (state: RootState) => state.updateDeliveryVoucher
  );

  const viewVoucherDialogRef = React.useRef<ViewDeliveryVoucherDialogRef>(null);

  const handleView = (voucher: IDeliveryVoucher) => {
    viewVoucherDialogRef.current?.open(voucher);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) setFile(selectedFile);
  };

  const handleUpload = () => {
    if (!file) return;

    const payload = {
      image: file,
      origin: 'MANUAL',
      chave_nf: invoice?.nfeId!,
    };

    dispatch(CreateAction.request(payload, handleRequest));
  };

  const handleApprove = (voucher: IDeliveryVoucher) => {
    dispatch(
      UpdateDeliveryVoucherActions.request(
        voucher.id,
        { action: 'approve' },
        handleRequest
      )
    );
  };

  const handleReject = (
    { deliveryVoucherId, ...rest }: RejectionProfPayload,
    callback: () => void
  ) => {
    dispatch(
      UpdateDeliveryVoucherActions.request(
        deliveryVoucherId,
        { action: 'reject', ...rest },
        handleRequest,
        ()=> {
          resetDeliveryVoucherStoreState();
          callback();
        }
      )
    );
  };

  const handleDownload = (voucher: IDeliveryVoucher) => {
    mutateDownload.mutate(voucher.id);
  };

  const handleRequest = React.useCallback(() => {
    const invoiceId = invoice?.id;
    if (!invoiceId || !canList) return;
    dispatch(ListDeliveryVouchersActions.request({ invoiceId }));
  }, [canList, dispatch, invoice?.id]);

  React.useEffect(() => {
    handleRequest();
  }, [handleRequest]);

  if (!invoice || !canList) return <></>;

  return (
    <>
      <Card {...props}>
        <CardContent className="pt-6">
          <Title>Upload Canhoto</Title>

          <div className="mt-4 space-y-6">
            <VoucherInput
              file={file}
              loading={creating}
              onChange={handleFileChange}
              onUpload={handleUpload}
              disabled={creating}
            />
            <DeliveryVoucherList
              vouchers={vouchers}
              onApprove={handleApprove}
              onReject={handleReject}
              onDownload={handleDownload}
              onView={handleView}
              loading={creating || updating}
              canDownload={canDownload}
              canUpdate={canUpdate}
            />
          </div>
        </CardContent>
      </Card>
      
      <ViewDeliveryVoucherDialog ref={viewVoucherDialogRef} />
    </>
  );
};
